import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { fetchData } from '../../helpers/apiUtils';
import { NfaRecommendation, NfaRecommendationResponse } from '../../helpers/nfaMailingData';

interface Props {
  onSelect: (mailing: NfaRecommendation, plannedDate: string) => void;
}

const MailingSelector: FC<Props> = ({ onSelect }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<NfaRecommendationResponse | null>(null);
  const [selectedItem, setSelectedItem] = useState<NfaRecommendation | null>(null);
  const [plannedDate, setPlannedDate] = useState(() => {
    // Default to next Tuesday
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysUntilTuesday = (2 - dayOfWeek + 7) % 7 || 7;
    const nextTuesday = new Date(now);
    nextTuesday.setDate(now.getDate() + daysUntilTuesday);
    return nextTuesday.toISOString().split('T')[0];
  });
  const [showAlternatives, setShowAlternatives] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const result = await fetchData<NfaRecommendationResponse>('/api/nfa-mailings/recommendation');
      if (cancelled) return;
      if (result.success && result.data) {
        setRecommendation(result.data);
        if (result.data.recommended) {
          setSelectedItem(result.data.recommended);
        }
      } else {
        setError(result.error || 'Fehler beim Laden der Empfehlung');
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!recommendation || recommendation.no_content_available) {
    return (
      <Alert severity="info">
        Aktuell ist kein Mailing verf&uuml;gbar. Es gibt keine anstehenden Live-Webinare mit
        Mailing-Copy und keine PLX-Mailings aus den letzten 3 Monaten, die noch nicht &uuml;ber NFA
        versendet wurden.
      </Alert>
    );
  }

  const renderMailingCard = (
    item: NfaRecommendation,
    isSelected: boolean,
    isRecommended: boolean,
  ) => (
    <Card
      key={`${item.source_type}-${item.source_id}`}
      sx={{
        mb: 2,
        cursor: 'pointer',
        border: isSelected ? '2px solid #2d6a4f' : '1px solid #e0e0e0',
        backgroundColor: isSelected ? '#f0f7f4' : '#ffffff',
        '&:hover': { borderColor: '#2d6a4f' },
      }}
      onClick={() => setSelectedItem(item)}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Chip
            label={item.source_type === 'webinar' ? 'Webinar' : 'PLX'}
            size="small"
            color={item.source_type === 'webinar' ? 'primary' : 'secondary'}
          />
          {isRecommended && <Chip label="Empfohlen" size="small" color="success" />}
        </Box>
        <Typography variant="h6" fontWeight="bold">
          {item.subject}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Kampagne: {item.campaign_name}
        </Typography>
        {item.product_name && (
          <Typography variant="body2" color="textSecondary">
            Produkt: {item.product_name} {item.product_code ? `(${item.product_code})` : ''}
          </Typography>
        )}
        {item.webinar_title && (
          <Typography variant="body2" color="textSecondary">
            Webinar: {item.webinar_title}
            {item.webinar_date ? ` — ${item.webinar_date}` : ''}
          </Typography>
        )}
        {item.performance && (
          <Box mt={1} display="flex" gap={2}>
            <Typography variant="caption" color="textSecondary">
              ANFOs: {item.performance.order_count}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Empf&auml;nger: {item.performance.total_recipients?.toLocaleString('de-DE')}
            </Typography>
            {item.performance.anfo_rate != null && (
              <Typography variant="caption" color="textSecondary">
                ANFO-Rate: {(item.performance.anfo_rate * 100).toFixed(3)}%
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {recommendation.has_mailing_this_week && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Sie haben diese Woche bereits ein Mailing vorbereitet. M&ouml;chten Sie ein weiteres
          vorbereiten?
        </Alert>
      )}

      <Typography variant="h6" mb={2}>
        Empfohlenes Mailing
      </Typography>

      {recommendation.recommended &&
        renderMailingCard(
          recommendation.recommended,
          selectedItem?.source_id === recommendation.recommended.source_id &&
            selectedItem?.source_type === recommendation.recommended.source_type,
          true,
        )}

      {recommendation.alternatives.length > 0 && (
        <>
          <Button
            size="small"
            startIcon={showAlternatives ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowAlternatives(!showAlternatives)}
            sx={{ mb: 1 }}
          >
            {showAlternatives
              ? 'Alternativen ausblenden'
              : `${recommendation.alternatives.length} Alternativen anzeigen`}
          </Button>
          <Collapse in={showAlternatives}>
            {recommendation.alternatives.map((alt) =>
              renderMailingCard(
                alt,
                selectedItem?.source_id === alt.source_id &&
                  selectedItem?.source_type === alt.source_type,
                false,
              ),
            )}
          </Collapse>
        </>
      )}

      <Box mt={3} display="flex" alignItems="center" gap={2}>
        <TextField
          label="Geplantes Versanddatum"
          type="date"
          value={plannedDate}
          onChange={(e) => setPlannedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ width: 220 }}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedItem || !plannedDate}
          onClick={() => {
            if (selectedItem && plannedDate) {
              onSelect(selectedItem, plannedDate);
            }
          }}
        >
          Vorschau generieren
        </Button>
      </Box>
    </Box>
  );
};

export default MailingSelector;
